import { ReactElement, useCallback, useMemo } from 'react';
import { FormattedMessage } from 'intl';
import { CommonTranslationKeys, DocumentationTranslationKeys } from 'intl/keys';

import { ButtonText } from 'components/general';
import {
  DocumentationBrowserDialog,
  useDocumentationBrowserState,
} from 'components/documentation/DocumentationBrowser';
import { useDocumentationStore } from 'store/documentation';
import {
  isDirectoryItemType,
  isFileItemType,
} from 'store/documentation/model/types';
import { openDirectoryForm } from 'presentation/directory/openDirectoryInput';

interface DocumentationMoveDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

export function DocumentationMoveDialog({
  isOpen,
  onClose,
}: DocumentationMoveDialogProps): ReactElement {
  const moveSelectedToDirectory =
    useDocumentationStore.use.moveSelectedToDirectory();
  const selectedItems = useDocumentationStore.use.selectedItems();

  const browser = useDocumentationBrowserState({
    isOpen,
  });
  const { refreshCurrentDirectory } = browser;

  const hiddenDirectoryLocalIds = useMemo(() => {
    const ids = new Set<number>();

    selectedItems.forEach((item) => {
      if (isDirectoryItemType(item.type)) {
        ids.add(item.localId);
      }
    });

    return ids;
  }, [selectedItems]);

  const hiddenDocumentationLocalIds = useMemo(() => {
    const ids = new Set<number>();

    selectedItems.forEach((item) => {
      if (isFileItemType(item.type)) {
        ids.add(item.localId);
      }
    });

    return ids;
  }, [selectedItems]);

  const moveBrowser = useMemo(
    () => ({
      ...browser,
      directories: browser.directories.filter(
        (directory) => !hiddenDirectoryLocalIds.has(directory.localId)
      ),
      documentations: browser.documentations.filter(
        (documentation) =>
          !hiddenDocumentationLocalIds.has(documentation.localId)
      ),
    }),
    [browser, hiddenDirectoryLocalIds, hiddenDocumentationLocalIds]
  );

  const handleMove = useCallback(() => {
    const targetDirectoryId =
      browser.selectedDirectoryId ?? browser.currentDirectoryId;

    moveSelectedToDirectory({ _id: targetDirectoryId });
    onClose();
  }, [browser.currentDirectoryId, browser.selectedDirectoryId, moveSelectedToDirectory, onClose]);

  const handleCreateDirectory = useCallback(() => {
    openDirectoryForm(undefined, {
      parentId: browser.currentDirectoryId || undefined,
      onSubmitSuccess: refreshCurrentDirectory,
    });
  }, [browser.currentDirectoryId, refreshCurrentDirectory]);

  return (
    <DocumentationBrowserDialog
      isOpen={isOpen}
      onClose={onClose}
      titleFallbackId={CommonTranslationKeys.move}
      searchDataQa='move-to-documentation-search'
      browser={moveBrowser}
      leftActions={
        <ButtonText onClick={handleCreateDirectory}>
          <FormattedMessage id={DocumentationTranslationKeys.createDirectory} />
        </ButtonText>
      }
      confirmLabelId={CommonTranslationKeys.move}
      onConfirm={handleMove}
      confirmDisabled={browser.loading || Boolean(browser.loadError)}
      confirmLoading={browser.loading}
    />
  );
}
